import type { BatchStatus } from '@gsls/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge } from '../../../components/ui';
import { formatQuantity } from '../../../lib/format';
import { links } from '../../../lib/links';
import { openBatchesQuery } from '../queries';
import {
  RowTitle,
  WidgetCard,
  WidgetEmpty,
  WidgetError,
  WidgetList,
  WidgetSkeleton,
} from '../primitives';

/**
 * The batches worth looking at, ordered by what happens next rather than by
 * status name: something running outranks a draft waiting to start, which
 * outranks a finished batch waiting to be closed.
 */
const NEXT_ACTION: Record<string, { label: string; tone: 'accent' | 'muted' | 'green' }> = {
  draft: { label: 'Start', tone: 'muted' },
  in_progress: { label: 'Continue', tone: 'accent' },
  completed: { label: 'Close', tone: 'green' },
};

const ORDER: Record<string, number> = { in_progress: 0, draft: 1, completed: 2 };

const OPEN_STATUSES: BatchStatus[] = ['draft', 'in_progress', 'completed'];

export function BatchBoardWidget({ limit = 6, to }: { limit?: number; to?: string }) {
  const query = useQuery(openBatchesQuery);

  const rows = (query.data?.items ?? [])
    .filter((batch) => OPEN_STATUSES.includes(batch.status))
    .sort(
      (a, b) =>
        (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || a.code.localeCompare(b.code),
    )
    .slice(0, limit);

  return (
    <WidgetCard
      title="Open batches"
      description="Ordered by the next step each one needs."
      to={to}
      toLabel="All batches"
    >
      {query.isPending ? <WidgetSkeleton /> : null}
      {query.isError ? (
        <WidgetError label="Could not load batches." onRetry={() => void query.refetch()} />
      ) : null}
      {query.isSuccess && rows.length === 0 ? (
        <WidgetEmpty>No batches are open.</WidgetEmpty>
      ) : null}
      {rows.length > 0 ? (
        <WidgetList>
          {rows.map((batch) => {
            const next = NEXT_ACTION[batch.status] ?? { label: 'Open', tone: 'muted' as const };
            return (
              <li key={batch.id}>
                <Link
                  to={links.batch(batch.id)}
                  className="-mx-2 flex items-center gap-3 rounded-md px-2 py-3 transition hover:bg-brand-soft/40"
                >
                  <RowTitle
                    title={batch.code}
                    subtitle={
                      <>
                        <span className="font-mono">{batch.itemCode}</span> · {batch.itemName}
                      </>
                    }
                  />
                  <div className="shrink-0 text-right">
                    <p className="whitespace-nowrap text-sm font-semibold text-ink">
                      {formatQuantity(batch.targetQuantity, batch.uom)}
                    </p>
                    <Badge tone={next.tone}>{next.label}</Badge>
                  </div>
                </Link>
              </li>
            );
          })}
        </WidgetList>
      ) : null}
    </WidgetCard>
  );
}
